<template>
  <div class="n-data-table">
    <div class="data-table__container" v-if="isLoading">
      <div class="loading-spinner-container">
        <div class="loading-spinner">
          <n-loading-spinner no-margin />
        </div>
      </div>
    </div>
    <div class="data-table__container" v-if="hasResults">
      <table class="n-table">
        <thead>
          <tr>
            <th v-for="column in columns" :key="column.name" :class="{ 'th--with-icon': column.icon }">
              <div class="column-heading">
                <div v-if="column.sortable" class="data-table__column" :class="{ sortable: column.sortable }">
                  <div
                    role="button"
                    v-if="column.sortable"
                    class="column__sort-trigger"
                    tabindex="0"
                    @click="handleColumnClick(column.name)"
                    @keypress.enter="handleColumnClick(column.name)"
                    @keypress.space="handleColumnClick(column.name)"
                  >
                    <n-icon v-if="column.icon" :icon="column.icon"></n-icon>
                    {{ column.display }}
                    <div :class="getSortClass(column.name)"></div>
                  </div>
                </div>
                <div v-if="!column.sortable" class="data-table__column">
                  <n-icon v-if="column.icon" :icon="column.icon"></n-icon>
                  {{ column.display }}
                </div>
              </div>
            </th>
            <th v-if="$slots.actions"></th>
          </tr>
        </thead>
        <tbody>
          <tr class="data-item" v-for="row in items" :key="row.id">
            <td
              class="data-cell"
              :class="{ 'cell-wrap': column.wrappable }"
              v-for="column in columns"
              :key="column.name"
            >
              <!-- Default cell display (no slot, no formatter) -->
              <div v-if="!$slots[column.name] && !column.formatter" class="default-cell-display">
                <a class="clickable-cell" v-if="isCellClickable(row, column.clickable)" @click="cellClicked(row)">
                  {{ row[column.name] }}
                </a>
                <span v-if="!isCellClickable(row, column.clickable)">
                  {{ row[column.name] }}
                </span>
              </div>

              <!-- Formatter cell display -->
              <div v-else-if="!$slots[column.name] && column.formatter">
                <a class="clickable-cell" v-if="isCellClickable(row, column.clickable)" @click="cellClicked(row)">
                  {{ column.formatter(row[column.name]) }}
                </a>
                <span v-if="!isCellClickable(row, column.clickable)">
                  {{ column.formatter(row[column.name]) }}
                </span>
              </div>

              <!-- Named slot rendering -->
              <slot v-else :name="column.name" v-bind:row="row"></slot>
            </td>

            <!-- Render actions slot separately -->
            <td v-if="$slots.actions" class="actions-column">
              <slot name="actions" v-bind:row="row"></slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <n-pagination
      v-if="pagination && hasResults"
      @page-changed="onPageChanged"
      :itemsTotal="itemsTotal"
      :itemsPerPage="itemsPerPage"
    ></n-pagination>

    <slot v-if="!hasResults && !isLoading" name="nodata" />
  </div>
</template>

<script lang="ts" setup>
import { PropType, ref, watch, onMounted, computed } from 'vue';
import NPagination, { Page } from './pagination.vue';
import DataTableColumn from './DataTableColumn';
import NLoadingSpinner from '../loading-spinner/loading-spinner.vue';
import PageData from './PageData';
import NIcon from '../../brand/icon/icon.vue';

defineOptions({
  name: 'n-data-table',
});

const props = defineProps({
  columns: {
    type: Array as PropType<DataTableColumn[]>,
    required: true,
  },
  items: {
    type: Array as PropType<any[]>,
    required: true,
  },
  itemsTotal: {
    type: Number,
    default: 0,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  pagination: {
    type: Boolean,
    default: false,
  },
  itemsPerPage: {
    type: Number,
    default: 0,
  },
  sortBy: {
    type: String,
  },
  sortDesc: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (e: 'load', data: PageData): void;
  (e: 'cellClicked', row: any): void;
}>();

const isLoading = ref(false);
const offset = ref(0);
const sortColumn = ref<string>();
const sortDirection = ref<'asc' | 'desc'>('asc');

watch(
  () => props.loading,
  (loading: boolean) => (isLoading.value = loading),
  { immediate: true },
);

watch(
  () => props.items,
  () => (isLoading.value = false),
  { immediate: true },
);

onMounted(() => {
  sortColumn.value = props.sortBy;
  sortDirection.value = props.sortDesc ? 'desc' : 'asc';
});

async function onPageChanged(page: Page) {
  offset.value = page.start;
  await loadData();
}

function loadData() {
  isLoading.value = true;
  emit('load', {
    offset: offset.value,
    sortColumn: sortColumn.value,
    sortDirection: sortDirection.value,
  } as PageData);
}

function getSortClass(column: string) {
  if (column !== sortColumn.value) {
    return '';
  }
  return sortDirection.value;
}

function handleColumnClick(column: string) {
  if (column === sortColumn.value) {
    if (sortDirection.value === 'desc') {
      sortDirection.value = 'asc';
    } else if (sortDirection.value === 'asc') {
      sortDirection.value = 'desc';
    }
  } else {
    sortDirection.value = 'desc';
    sortColumn.value = column;
  }
  loadData();
}

function isCellClickable(row: any, clickableConfig?: boolean | ((row: any) => boolean)): boolean {
  if (typeof clickableConfig === 'function') {
    return clickableConfig(row);
  }
  return !!clickableConfig;
}

function cellClicked(row: any) {
  emit('cellClicked', row);
}

const hasResults = computed(() => {
  if (props.pagination) {
    return props.itemsTotal > 0 && props.items && props.items.length > 0;
  }
  return props.items && props.items.length > 0;
});
</script>

<style scoped lang="scss">
@use '../../../styles/colours' as *;

.n-table {
  border-collapse: collapse;
  width: 100%;
  td,
  th {
    white-space: nowrap;
    border: 1px solid colourScheme('grey', 'light');
    border-width: 0 0 1px;
    padding: 0.5em 0.25em;
    vertical-align: top;
    color: #363636;
    &.cell-wrap {
      white-space: normal;
    }
    &.th--with-icon {
      padding: 0.5em 0;
    }
  }

  td {
    border: 1px solid colourScheme('grey', 'light');
    border-width: 0 0 1px;
    padding: 0.5em 0.25em;
    vertical-align: top;
    &.actions-column {
      width: 48px;
      text-align: left;
    }
  }
}

.column-heading {
  display: flex;
}

.data-table__column,
.column__sort-trigger {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  height: 20px;
  gap: 5px;
}

.sortable {
  user-select: none;
  color: black;
  cursor: pointer;
  &:hover {
    color: colourScheme('link');

    .desc {
      border-top: 6px solid colourScheme('link');
    }

    .asc {
      border-bottom: 6px solid colourScheme('link');
    }
  }

  .desc {
    margin-left: 5px;
    margin-top: 6px;
    border: 5px solid transparent;
    border-top: 6px solid black;
  }

  .asc {
    margin-left: 5px;
    display: flex;
    border: 5px solid transparent;
    border-bottom: 6px solid black;
    margin-bottom: 5px;
  }
}

:deep(.sk-folding-cube) {
  margin: 6rem auto;
}

.data-table {
  position: relative;
  width: 100%;
}

.data-table-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;

  .loading-spinner-container {
    display: flex;
    justify-content: center;
    min-height: 250px;

    .loading-spinner {
      width: 50px;
      height: 50px;
    }
  }
}

:deep(.dropdown) {
  position: absolute;

  .dropdown-trigger {
    position: relative;
    top: -2px;
  }

  .dropdown-menu {
    top: calc(100% - 5px);
  }
}
</style>
